import React, { useMemo, useState } from 'react'
import {
  getIntegrationServiceIdsForCategory,
  IntegrationCategory,
  IntegrationServiceId,
  isIntegrationOnlyDisconnect,
  isIntegrationUnConnectable,
} from '../../../common/integrations'
import './IntegrationCard.scss'
import { IntegrationIcon } from '../../IntegrationIcon'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { IntegrationConnection } from '../../../views/Integration/Connectors/IntegrationConnection'
import { IntegrationCardDescription } from './IntegrationCardDescription'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { IntegrationState } from '../IntegrationState'
import { IntegrationSpecificSetting } from '../../../views/Integration/IntegrationManagement/IntegrationSpecificSettings/IntegrationSpecificSetting'
import { CommonCard } from '../../Card/CommonCard'
import { ConnectButton, ManageButton } from '../../Button/IntegrationAndOAuth2AppButtons'

export interface IntegrationCardProps {
  serviceId: IntegrationServiceId
}

export const IntegrationCard: React.FC<IntegrationCardProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isLoading: userIsLoading } = useGetMeQuery({ accountId })
  const { hasScope } = useZocksAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const hasIntegrationConnectScope = useMemo(() => {
    return hasScope('**', 'integration:connect', ['integration:admin'])
  }, [hasScope])

  const currentIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => (i.serviceId as IntegrationServiceId) === serviceId)
  }, [userData, serviceId])

  const hasIntegration = useMemo(() => {
    return !!currentIntegration
  }, [currentIntegration])

  const disableBecauseOtherMailIntegration = useMemo(() => {
    const mailIntegrationServiceIds = getIntegrationServiceIdsForCategory(IntegrationCategory.MAIL)

    if (!mailIntegrationServiceIds.includes(serviceId)) {
      return false
    }

    return !!userData?.integrations?.some((i) => i.type === 'mail' && i.serviceId !== serviceId)
  }, [userData, serviceId])

  const disableConnectButton = useMemo(() => {
    return disableBecauseOtherMailIntegration || !hasIntegrationConnectScope
  }, [disableBecauseOtherMailIntegration, hasIntegrationConnectScope])

  const disableManageButton = useMemo(() => {
    return !hasIntegrationConnectScope
  }, [hasIntegrationConnectScope])

  const hasSettingsSections = useMemo(() => IntegrationSpecificSetting({ serviceId }), [serviceId])

  const manageButtonTo =
    hasSettingsSections.length > 0
      ? `/account/integration-settings/integrations/${serviceId}/settings`
      : `/account/integration-settings/integrations/${serviceId}/about`

  const [startConnect, setStartConnect] = useState<boolean>(() => {
    const crm = searchParams.get('crm')
    const connector = searchParams.get('connector')
    const validCRM = crm && Object.values<string>(IntegrationServiceId).includes(crm) ? (crm as IntegrationServiceId) : null
    const validConnector = connector && Object.values<string>(IntegrationServiceId).includes(connector) ? (connector as IntegrationServiceId) : null

    const shouldStartConnect = validCRM === serviceId || validConnector === serviceId

    if (shouldStartConnect && serviceId === IntegrationServiceId.OUTLOOK_CALENDAR && searchParams.get('connect') !== 'true') {
      return false
    }

    return shouldStartConnect
  })

  const onConnected = () => {
    setStartConnect(false)
  }

  const handleCardClick = () => {
    navigate(`/account/integration-settings/integrations/${serviceId}/about`)
  }

  if (userIsLoading) {
    return <div className='skeleton' style={{ width: '100%', height: '60px' }} />
  }

  const hideConnectOrManageButton = isIntegrationUnConnectable(serviceId) || isIntegrationOnlyDisconnect(serviceId)

  const footer = hideConnectOrManageButton ? null : hasIntegration ? (
    <ManageButton to={manageButtonTo} isDisabled={disableManageButton} />
  ) : (
    <ConnectButton onClick={() => setStartConnect(true)} isDisabled={disableConnectButton} />
  )

  return (
    <>
      <IntegrationConnection serviceId={serviceId} startConnect={startConnect} onConnected={onConnected} />

      <CommonCard
        onCardClick={handleCardClick}
        header={<IntegrationIcon serviceId={serviceId} className='integration-settings-icon' />}
        description={<IntegrationCardDescription serviceId={serviceId} />}
        state={<IntegrationState currentIntegration={currentIntegration} className='d-flex flex-row align-items-start gap-1 mt-3' />}
        footer={footer}
      />
    </>
  )
}
